'use client'

import { type ComponentProps, useCallback } from 'react'
import { toast } from 'react-toastify'

import { cn } from '@/shared/lib'

export type Props = Omit<ComponentProps<'div'>, 'onChange'> & {
  onChange?: ({ file, files }: { file: File; files: File[] }) => Promise<void>
  accept?: string
  maxSize?: number
  disabled?: boolean
  multiple?: boolean
  onAcceptError?: () => void
}

const isFileAcceptable = (file: File, accept: string) => {
  if (accept === '*/*') {
    return true
  }

  const acceptedTypes = accept.split(',').map(type => type.trim())
  const fileType = file.type
  const fileName = file.name.toLowerCase()

  const isTypeValid = acceptedTypes.some(type => {
    if (type.startsWith('.')) {
      // 확장자 기반 체크 (예: .pdf, .jpg)
      return fileName.endsWith(type.toLowerCase())
    } else if (type.endsWith('/*')) {
      // MIME 타입 패턴 체크 (예: image/*, application/*)
      const [baseType] = type.split('/')

      return fileType.startsWith(baseType)
    } else {
      // MIME 타입 체크 (예: image/jpeg, application/pdf)
      return fileType === type
    }
  })

  return isTypeValid
}

const getSafeInputMultiple = (multiple: boolean): boolean => {
  const userAgent = navigator?.userAgent || ''
  const samsungBrowserMatch = userAgent.match(/SamsungBrowser\/(\d+)/)

  // 삼성 인터넷 v29버전 이상에서 multiple 옵션이 있다면 갤러리에서 파일을 불러오면 input이 멈추는 이슈
  if (samsungBrowserMatch) {
    const version = parseInt(samsungBrowserMatch[1])

    return version >= 29 ? false : multiple
  }

  return multiple
}

const defaultOnAcceptError = () => {
  toast('지원하지 않는 파일 형식입니다.')
}

export const Upload = ({
  ref,
  className,
  onChange,
  accept = '*/*',
  maxSize,
  disabled = false,
  multiple = false,
  onAcceptError = defaultOnAcceptError,
  children,
  ...props
}: Props) => {
  const handleFileSelect = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        if (maxSize && file.size > maxSize) {
          const maxSizeMB = `${Math.floor(maxSize / 1024 / 1024)}MB`

          toast(`파일 크기는 ${maxSizeMB} 이하만 가능합니다.`)

          return
        }

        if (!isFileAcceptable(file, accept)) {
          onAcceptError()

          return
        }
      }

      await onChange?.({ file: files[0], files })
    },
    [accept, maxSize, onChange, onAcceptError],
  )

  const handleClick = useCallback(() => {
    if (disabled) {
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.style.display = 'none'
    input.multiple = getSafeInputMultiple(multiple)

    input.addEventListener('change', async event => {
      const target = event.target as HTMLInputElement
      const files = Array.from(target.files || [])

      if (files.length > 0) {
        const stabilizedFiles = await Promise.all(
          Array.from(files).map(file => stabilizeFile(file)),
        )

        await handleFileSelect(stabilizedFiles)
      }

      // 파일 선택 후 input 제거
      document.body.removeChild(input)
    })

    // 클릭 취소 시에도 input 제거
    input.addEventListener('cancel', () => {
      if (document.body.contains(input)) {
        document.body.removeChild(input)
      }
    })

    document.body.appendChild(input)
    input.click()
  }, [disabled, accept, multiple, handleFileSelect])

  const stabilizeFile = async (file: File) => {
    try {
      const buffer = await readFileAsArrayBuffer(file)

      return new File([buffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      })
    } catch (error) {
      console.error('Failed to stabilize file:', file.name, error)

      throw error
    }
  }

  const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'))
        }
      }

      reader.onerror = () => {
        reject(reader.error || new Error('Failed to read file'))
      }

      reader.readAsArrayBuffer(file)
    })
  }

  return (
    <div
      ref={ref}
      className={cn('relative cursor-pointer', className)}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  )
}
